import compact from 'lodash/compact';
import head from 'lodash/head';
import isNil from 'lodash/isNil';
import { v4 as uuidv4 } from 'uuid';

import { Colors } from '../colorUtils';
import { contactContainsType } from '../contact/contactutils';
import { POSTING_TABLE_NAME, getReadId, getTableClientId } from '../databaseUtils';
import {
  addCalendarDate,
  calendarDateFromTimestamp,
  endOfCalendarMonth,
  fromCalendarDate,
  fromDateTime,
  isAfter,
  startOfCalendarMonth,
  toCalendarDate,
  today,
  todayDateTime,
} from '../dateutils';
import { roundAtSecondDecimal } from '../numbers';
import { isNilOrEmpty } from '../objectUtils';
import {
  AccountLabel,
  BankAccount,
  ContactType,
  InvoiceWithPostings,
  Posting,
  PostingForeignType,
  PostingStatus,
  PostingType,
  Transaction,
  TransactionFlowType,
  TransactionLink,
  TransactionLinkType,
  TransactionOrigin,
  TransactionStatus,
} from '../types';

import { ACCOUNT_BANK_TOPCLASS, ACCOUNT_LEASE_TOPCLASS, ACCOUNT_SUSPENSE_TOPCLASS } from './accountClasses';
import { getOwnerAccountLabelToWorkWith } from './accountLabelUtils';
import { LinkType } from './accountutils';
import { orderResolutionsByFlowType, RESOLUTION_KIND_BY_LINK_TYPE } from './flowClassification';

const SNOOZE_DAYS_PER_WEEK = 7;

export const getSnoozeUntilDate = (weeks: number): string =>
  fromCalendarDate(addCalendarDate(today(), weeks * SNOOZE_DAYS_PER_WEEK, 'days'));

export const isSnoozeStillRunning = (snoozedUntil: string | null | undefined): boolean => {
  if (isNilOrEmpty(snoozedUntil)) {
    return false;
  }
  return isAfter(toCalendarDate(snoozedUntil), today());
};

export const isTransactionToReconcile = (transaction: Pick<Transaction, 'status' | 'snoozedUntil'>): boolean => {
  if (transaction.status === TransactionStatus.TO_RECONCILE) {
    return true;
  }
  return transaction.status === TransactionStatus.SNOOZED && !isSnoozeStillRunning(transaction.snoozedUntil);
};

export const isTransactionSnoozed = (transaction: Pick<Transaction, 'status' | 'snoozedUntil'>): boolean =>
  transaction.status === TransactionStatus.SNOOZED && isSnoozeStillRunning(transaction.snoozedUntil);

export const getStatusFromAmount = (reconciledAmount: number, transactionAmount: number): TransactionStatus => {
  if (roundAtSecondDecimal(reconciledAmount) === roundAtSecondDecimal(transactionAmount)) {
    return TransactionStatus.RECONCILED;
  }
  if (reconciledAmount !== 0) {
    return TransactionStatus.PARTIALLY_RECONCILED;
  }
  return TransactionStatus.TO_RECONCILE;
};

export interface SplitTransactionValues {
  totalAmount: number;
  links: LinkValue[];
}

export interface TransactionWithAmountLinked extends Transaction {
  amountLinked: number;
  amountToBeReconciled: number;
}

export type TransactionWithoutPostings = Omit<Transaction, 'postings'>;

export interface LinkValue {
  type?: LinkType;
  id?: string; // contact id
  invoiceMatches?: InvoiceWithPostings[];
  // All matched invoices (for split transactions that can combine multiple invoices)
  amount: number;
}

const isTransactionLinkedToSomething = (transaction: Transaction) => !isNilOrEmpty(transaction.links);

const getPostingPeriodDate = (transaction: Transaction): string =>
  fromCalendarDate(
    isNilOrEmpty(transaction.operationDate) ? today() : calendarDateFromTimestamp(transaction.operationDate)
  );

const getLeaseContractorOrOwnerPosting = (
  linkType: TransactionLinkType,
  transaction: Transaction,
  allAccountLabels: AccountLabel[],
  isTransactionNegative: boolean,
  absAmount: number,
  linkId: string,
  unitId?: string
) => {
  switch (linkType) {
    case TransactionLinkType.LEASE:
      return buildLeasePosting(
        transaction,
        allAccountLabels,
        isTransactionNegative ? PostingType.DEBIT : PostingType.CREDIT,
        absAmount,
        linkId,
        undefined,
        unitId
      );
    case TransactionLinkType.CONTACT:
      return buildContractorPosting(
        transaction,
        allAccountLabels,
        isTransactionNegative ? PostingType.DEBIT : PostingType.CREDIT,
        absAmount,
        linkId
      );
    case TransactionLinkType.OWNER:
      return buildOwnerPosting(
        transaction,
        allAccountLabels,
        isTransactionNegative ? PostingType.DEBIT : PostingType.CREDIT,
        absAmount,
        linkId
      );
    default:
      return undefined;
  }
};

export enum IngestionPostingRole {
  BANK = 'bank',
  COUNTERPART = 'counterpart',
  LETTERING_LEASE = 'lettering-lease',
  SETTLEMENT = 'settlement',
  PENDING_BALANCE = 'pending-balance',
}

// The postings booked at ingestion are keyed by transaction and role: a retry after a crash between the
// postings write and the transaction insert rewrites the same items instead of adding a second set
export const buildIngestionPostingId = (
  transactionId: string,
  role: IngestionPostingRole,
  discriminator?: string
): string => compact([transactionId, role, discriminator]).join('#');

const PENDING_BALANCE_TRANSACTION_SUFFIX = 'pending-balance';

export const buildIngestionPendingBalanceTransactionId = (transactionId: string): string =>
  `${transactionId}-${PENDING_BALANCE_TRANSACTION_SUFFIX}`;

export interface PendingBalanceStub {
  transaction: Pick<
    Transaction,
    'id' | 'status' | 'amount' | 'links' | 'statementDate' | 'operationDate' | 'bankAccountId' | 'origin'
  >;
  posting: Pick<
    Posting,
    | 'leaseId'
    | 'bankAccountId'
    | 'type'
    | 'accountLabelId'
    | 'class'
    | 'topClass'
    | 'transactionId'
    | 'totalAmount'
    | 'periodFrom'
    | 'periodTo'
    | 'foreignId'
    | 'foreignType'
  >;
}

export const buildPendingBalanceStub = (
  stubTransactionId: string,
  originalTransactionId: string,
  bankAccountId: string,
  leaseId: string,
  amount: number,
  accountLabel: Pick<AccountLabel, 'id' | 'class' | 'topClass'>
): PendingBalanceStub => {
  const currentDate = today();
  return {
    transaction: {
      id: stubTransactionId,
      status: TransactionStatus.IGNORE,
      amount,
      links: [{ amount, linkId: leaseId, linkType: TransactionLinkType.LEASE }] as TransactionLink[],
      statementDate: fromCalendarDate(currentDate),
      operationDate: fromDateTime(todayDateTime()),
      bankAccountId,
      origin: TransactionOrigin.BANK,
    },
    posting: {
      leaseId,
      bankAccountId,
      type: PostingType.DEBIT,
      accountLabelId: accountLabel.id,
      class: accountLabel.class,
      topClass: accountLabel.topClass,
      transactionId: stubTransactionId,
      totalAmount: Math.abs(amount),
      periodFrom: fromCalendarDate(startOfCalendarMonth(currentDate)),
      periodTo: fromCalendarDate(endOfCalendarMonth(currentDate)),
      foreignId: originalTransactionId,
      foreignType: PostingForeignType.TRANSACTION_PENDING_BALANCE,
    },
  };
};

export const getPostingsToCreateForNewTransaction = (
  transaction: Transaction,
  allAccountLabels: AccountLabel[],
  amount: number,
  linkType?: TransactionLinkType,
  linkId?: string,
  unitId?: string
): Posting[] => {
  const isTransactionNegative = amount < 0;
  const absAmount = Math.abs(amount);
  const withRole = (posting: Posting, role: IngestionPostingRole): Posting => ({
    ...posting,
    id: buildIngestionPostingId(transaction.id, role),
  });
  if (!isTransactionLinkedToSomething(transaction)) {
    const suspensePosting = buildSuspensePosting(
      transaction,
      allAccountLabels,
      isTransactionNegative ? PostingType.DEBIT : PostingType.CREDIT,
      absAmount
    );
    const bankPosting = buildBankPosting(
      allAccountLabels,
      transaction,
      isTransactionNegative ? PostingType.CREDIT : PostingType.DEBIT,
      absAmount,
      linkType,
      linkId
    );
    return [
      withRole(suspensePosting, IngestionPostingRole.COUNTERPART),
      withRole(bankPosting, IngestionPostingRole.BANK),
    ];
  }
  if (linkId && linkType) {
    const leaseContractorOwnerPosting = getLeaseContractorOrOwnerPosting(
      linkType,
      transaction,
      allAccountLabels,
      isTransactionNegative,
      absAmount,
      linkId,
      unitId
    );

    const bankPosting = buildBankPosting(
      allAccountLabels,
      transaction,
      isTransactionNegative ? PostingType.CREDIT : PostingType.DEBIT,
      absAmount,
      linkType,
      linkId
    );
    return compact([
      isNil(leaseContractorOwnerPosting)
        ? null
        : withRole(leaseContractorOwnerPosting, IngestionPostingRole.COUNTERPART),
      withRole(bankPosting, IngestionPostingRole.BANK),
    ]);
  }
  return [];
};

const buildBankPosting = (
  allAccountLabels: AccountLabel[],
  transaction: Transaction,
  postingType: PostingType,
  postingAmount: number,
  linkType?: TransactionLinkType,
  linkId?: string
) => {
  const bankAccountLabel = allAccountLabels.find(
    (accountLabel) => accountLabel.bankAccountId === transaction.bankAccountId
  )!;
  const periodDate = getPostingPeriodDate(transaction);
  const bankPosting: Posting = {
    id: uuidv4(),
    accountLabelId: bankAccountLabel?.id,
    bankAccountId: transaction.bankAccountId,
    class: bankAccountLabel?.class,
    periodFrom: periodDate,
    periodTo: periodDate,
    topClass: ACCOUNT_BANK_TOPCLASS,
    transactionId: transaction.id,
    type: postingType,
    totalAmount: postingAmount,
    clientId: getTableClientId(transaction.clientId, POSTING_TABLE_NAME),
    readId: getReadId(transaction.clientId, POSTING_TABLE_NAME),
  };
  if (linkType === TransactionLinkType.LEASE) {
    bankPosting.leaseId = linkId;
  }
  if (linkType && [TransactionLinkType.CONTACT, TransactionLinkType.OWNER].includes(linkType)) {
    bankPosting.contactId = linkId;
  }
  return bankPosting;
};

const buildSuspensePosting = (
  transaction: Transaction,
  allAccountLabels: AccountLabel[],
  postingType: PostingType,
  postingAmount: number,
  postingStatus?: PostingStatus
) => {
  const suspenseAccountLabel = allAccountLabels.find(
    (accountLabel) => accountLabel.class === ACCOUNT_SUSPENSE_TOPCLASS
  )!;
  const periodDate = getPostingPeriodDate(transaction);
  const suspensePosting: Posting = {
    id: uuidv4(),
    accountLabelId: suspenseAccountLabel?.id,
    amountVatExcluded: Math.abs(postingAmount),
    class: suspenseAccountLabel?.class,
    topClass: suspenseAccountLabel?.topClass,
    periodFrom: periodDate,
    periodTo: periodDate,
    status: postingStatus,
    totalAmount: Math.abs(postingAmount),
    transactionId: transaction.id,
    type: postingType,
    clientId: getTableClientId(transaction.clientId, POSTING_TABLE_NAME),
    readId: getReadId(transaction.clientId, POSTING_TABLE_NAME),
  };
  return suspensePosting;
};

const buildLeasePosting = (
  transaction: Transaction,
  allAccountLabels: AccountLabel[],
  postingType: PostingType,
  postingAmount: number,
  leaseId: string,
  postingStatus?: PostingStatus,
  unitId?: string
) => {
  const leaseAccountLabel = allAccountLabels.find((accountLabel) => accountLabel.class === ACCOUNT_LEASE_TOPCLASS)!;
  const periodDate = getPostingPeriodDate(transaction);
  const leasePosting: Posting = {
    id: uuidv4(),
    accountLabelId: leaseAccountLabel?.id,
    amountVatExcluded: postingAmount,
    class: leaseAccountLabel?.class,
    topClass: leaseAccountLabel?.topClass,
    periodFrom: periodDate,
    periodTo: periodDate,
    leaseId,
    status: postingStatus,
    totalAmount: postingAmount,
    transactionId: transaction.id,
    type: postingType,
    unitId,
    clientId: getTableClientId(transaction.clientId, POSTING_TABLE_NAME),
    readId: getReadId(transaction.clientId, POSTING_TABLE_NAME),
  };
  return leasePosting;
};

const buildContractorPosting = (
  transaction: Transaction,
  allAccountLabels: AccountLabel[],
  postingType: PostingType,
  postingAmount: number,
  contractorId: string,
  postingStatus?: PostingStatus
) => {
  const contractorAccountLabel = allAccountLabels.find((accountLabel) => accountLabel.contactId === contractorId)!;
  const periodDate = getPostingPeriodDate(transaction);
  const contractorPosting: Posting = {
    id: uuidv4(),
    accountLabelId: contractorAccountLabel?.id,
    amountVatExcluded: Math.abs(postingAmount),
    contactId: contractorId,
    class: contractorAccountLabel?.class,
    topClass: contractorAccountLabel?.topClass,
    periodFrom: periodDate,
    periodTo: periodDate,
    status: postingStatus,
    totalAmount: Math.abs(postingAmount),
    transactionId: transaction.id,
    type: postingType,
    clientId: getTableClientId(transaction.clientId, POSTING_TABLE_NAME),
    readId: getReadId(transaction.clientId, POSTING_TABLE_NAME),
  };
  return contractorPosting;
};

const buildOwnerPosting = (
  transaction: Transaction,
  allAccountLabels: AccountLabel[],
  postingType: PostingType,
  postingAmount: number,
  ownerId: string,
  postingStatus?: PostingStatus
) => {
  const agencyToOwner = transaction.amount < 0;

  const ownerAccountLabel = getOwnerAccountLabelToWorkWith(allAccountLabels, agencyToOwner, ownerId)!;
  const periodDate = getPostingPeriodDate(transaction);
  const ownerPosting: Posting = {
    id: uuidv4(),
    accountLabelId: ownerAccountLabel.id,
    amountVatExcluded: Math.abs(postingAmount),
    contactId: ownerId,
    class: ownerAccountLabel.class,
    topClass: ownerAccountLabel.topClass,
    periodFrom: periodDate,
    periodTo: periodDate,
    status: postingStatus,
    totalAmount: Math.abs(postingAmount),
    transactionId: transaction.id,
    type: postingType,
    clientId: getTableClientId(transaction.clientId, POSTING_TABLE_NAME),
    readId: getReadId(transaction.clientId, POSTING_TABLE_NAME),
  };
  return ownerPosting;
};

export const getTransactionLinkFromLinkType = (currentLinkType: LinkType) => {
  switch (currentLinkType) {
    case LinkType.Lease:
      return TransactionLinkType.LEASE;
    case LinkType.Contractor:
      return TransactionLinkType.CONTACT;
    case LinkType.Owner:
      return TransactionLinkType.OWNER;
    default:
      throw new Error('Unsupported link type');
  }
};

export const getLinksForSplitTransaction = (links: LinkValue[]) =>
  links.reduce<{ contractorLinks: LinkValue[]; ownerLinks: LinkValue[]; leaseLinks: LinkValue[] }>(
    (acc, link) => {
      switch (link.type) {
        case LinkType.Lease:
          acc.leaseLinks.push(link);
          break;
        case LinkType.Contractor:
          acc.contractorLinks.push(link);
          break;
        case LinkType.Owner:
          acc.ownerLinks.push(link);
          break;
        default:
          break;
      }
      return acc;
    },
    { contractorLinks: [], ownerLinks: [], leaseLinks: [] }
  );

export interface ManagementFilterScope {
  ownerIds: string[];
  buildingIds: string[];
}

export const filterTransactionForManagementFilter = (
  transaction: Pick<Transaction, 'bankAccount'>,
  scope: ManagementFilterScope
) => {
  const bankAccount = transaction.bankAccount;
  if (isNil(bankAccount)) {
    return true;
  }
  return isBankAccountForManagementFilter(bankAccount, scope);
};

export const isBankAccountForManagementFilter = (
  bankAccount: Pick<BankAccount, 'contact' | 'contactIds' | 'buildingIds'>,
  { ownerIds, buildingIds }: ManagementFilterScope
): boolean => {
  if (isNilOrEmpty(ownerIds) && isNilOrEmpty(buildingIds)) {
    return true;
  }
  const holder = bankAccount.contact;
  const holderMatches = !isNil(holder) && ownerIds.includes(holder.id);
  const multiOwnerMatches = (bankAccount.contactIds ?? []).some((contactId) => ownerIds.includes(contactId));
  const buildingMatches = (bankAccount.buildingIds ?? []).some((buildingId) => buildingIds.includes(buildingId));
  if (holderMatches || multiOwnerMatches || buildingMatches) {
    return true;
  }
  const isHolderOwner = !isNil(holder) && contactContainsType(holder, ContactType.OWNER);
  return !isHolderOwner && isNilOrEmpty(bankAccount.contactIds) && isNilOrEmpty(bankAccount.buildingIds);
};

export const getProposedLinkId = (
  links: TransactionLink[] | null | undefined,
  transactionLinkType: TransactionLinkType,
  initialProposedLinkId?: string | null
) => (links?.length === 1 && links[0].linkType === transactionLinkType ? links[0].linkId : initialProposedLinkId);

const getProposedTransactionLinks = (
  transactionAmount: number,
  proposedLinkIds: Partial<Record<TransactionLinkType, string | null>>
): TransactionLink[] =>
  [TransactionLinkType.LEASE, TransactionLinkType.CONTACT, TransactionLinkType.OWNER].reduce<TransactionLink[]>(
    (acc, linkType) => {
      const linkId = proposedLinkIds[linkType];
      if (!isNilOrEmpty(linkId)) {
        acc.push({ amount: transactionAmount, linkId, linkType });
      }
      return acc;
    },
    []
  );

/** A movement proposed for several targets (a tenant who is also a supplier) preselects the one its flow type reads */
export const getInitialSelectedTransactionLink = (
  transactionAmount: number,
  transactionLinks?: TransactionLink[] | null,
  proposedLeaseId?: string | null,
  proposedContractorId?: string | null,
  proposedOwnerId?: string | null,
  flowType?: TransactionFlowType | null
): TransactionLink | null => {
  if (isNilOrEmpty(transactionLinks)) {
    const proposedLinks = getProposedTransactionLinks(transactionAmount, {
      [TransactionLinkType.LEASE]: proposedLeaseId,
      [TransactionLinkType.CONTACT]: proposedContractorId,
      [TransactionLinkType.OWNER]: proposedOwnerId,
    });
    const preselectedLink = head(
      orderResolutionsByFlowType(
        proposedLinks,
        flowType ?? TransactionFlowType.UNKNOWN,
        (link) => RESOLUTION_KIND_BY_LINK_TYPE[link.linkType]
      )
    );
    if (!isNil(preselectedLink)) {
      return preselectedLink;
    }
  }
  if (transactionLinks?.length === 1) {
    return transactionLinks[0];
  }
  return null;
};

export const getTransactionAmountColor = (amount: number, isPartiallyReconciled: boolean): Colors => {
  if (isPartiallyReconciled || amount < 0) {
    return Colors.CARROT_ORANGE;
  }
  if (amount > 0) {
    return Colors.TURQUOISE_BLUE;
  }
  return Colors.SILVER;
};

export interface ProposedAllocationPosting {
  id: string;
  amountSelected: number;
}

export interface ProposedTransactionAllocation {
  leaseId: string;
  invoiceIds: string[];
  postings: ProposedAllocationPosting[];
  amount: number;
  computedAt: string;
}

const isProposedAllocationPosting = (candidate: Partial<ProposedAllocationPosting>): boolean =>
  typeof candidate.id === 'string' && typeof candidate.amountSelected === 'number';

const isProposedTransactionAllocation = (
  candidate: Partial<ProposedTransactionAllocation>
): candidate is ProposedTransactionAllocation =>
  typeof candidate.leaseId === 'string' &&
  typeof candidate.amount === 'number' &&
  Array.isArray(candidate.invoiceIds) &&
  !isNilOrEmpty(candidate.invoiceIds) &&
  candidate.invoiceIds.every((invoiceId) => typeof invoiceId === 'string') &&
  Array.isArray(candidate.postings) &&
  !isNilOrEmpty(candidate.postings) &&
  candidate.postings.every(isProposedAllocationPosting);

export const getValidProposedAllocation = (
  transaction: Pick<Transaction, 'amount' | 'proposedAllocation' | 'proposedLeaseId'>
): ProposedTransactionAllocation | null => {
  const { amount, proposedAllocation, proposedLeaseId } = transaction;
  if (isNilOrEmpty(proposedAllocation) || isNil(proposedLeaseId)) {
    return null;
  }
  let candidate: Partial<ProposedTransactionAllocation>;
  try {
    let parsed = JSON.parse(proposedAllocation) as Partial<ProposedTransactionAllocation> | string;
    if (typeof parsed === 'string') {
      parsed = JSON.parse(parsed) as Partial<ProposedTransactionAllocation>;
    }
    candidate = parsed;
  } catch {
    return null;
  }
  if (isNil(candidate) || !isProposedTransactionAllocation(candidate)) {
    return null;
  }
  if (candidate.leaseId !== proposedLeaseId || candidate.amount !== amount) {
    return null;
  }
  const allocatedAmount = roundAtSecondDecimal(
    candidate.postings.reduce((total, posting) => total + posting.amountSelected, 0)
  );
  if (allocatedAmount !== roundAtSecondDecimal(amount)) {
    return null;
  }
  return candidate;
};
